import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  FileText, 
  Copy, 
  Check,
  Search,
  ShoppingBag,
  Utensils,
  Briefcase,
  GraduationCap,
  Heart,
  Home,
  Car,
  Plane,
  Smartphone,
  Sparkles,
  Gift,
  Calendar,
  Users,
  TrendingUp,
  Star,
  Zap,
  Plus,
  Trash2,
  Edit3,
  Save,
  FolderHeart,
  Clock,
  BarChart2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/toast";
import {
  getCustomTemplates,
  createCustomTemplate,
  updateCustomTemplate,
  deleteCustomTemplate,
  toggleTemplateFavorite,
  incrementTemplateUse
} from "@/lib/marketing-api";

// Utility function to detect text direction
const detectTextDirection = (text) => {
  if (!text) return 'ltr';
  // Check if the first significant character is Arabic
  const arabicPattern = /[\u0600-\u06FF]/;
  const firstChars = text.replace(/[^\w\u0600-\u06FF]/g, '').slice(0, 10);
  return arabicPattern.test(firstChars) ? 'rtl' : 'ltr';
};

// Template IDs for bilingual templates
const templateIds = [
  { id: "builtin-1", category: "ecommerce", popularity: 95 },
  { id: "builtin-2", category: "ecommerce", popularity: 92 },
  { id: "builtin-3", category: "ecommerce", popularity: 88 },
  { id: "builtin-4", category: "restaurants", popularity: 85 },
  { id: "builtin-5", category: "restaurants", popularity: 82 },
  { id: "builtin-6", category: "services", popularity: 78 },
  { id: "builtin-7", category: "education", popularity: 90 },
  { id: "builtin-8", category: "education", popularity: 87 },
  { id: "builtin-9", category: "health", popularity: 83 },
  { id: "builtin-10", category: "realestate", popularity: 76 },
  { id: "builtin-11", category: "travel", popularity: 84 },
  { id: "builtin-12", category: "tech", popularity: 81 },
  { id: "builtin-13", category: "events", popularity: 79 },
  { id: "builtin-14", category: "events", popularity: 91 },
  { id: "builtin-15", category: "all", popularity: 86 },
  { id: "builtin-16", category: "all", popularity: 77 },
  { id: "builtin-17", category: "all", popularity: 73 },
];

// Function to get bilingual templates
const getBuiltInTemplates = (t) => {
  return templateIds.map((template, index) => {
    const num = index + 1;
    return {
      id: template.id,
      category: template.category,
      title: t(`builtInTemplates.${num}.title`),
      description: t(`builtInTemplates.${num}.description`),
      content: t(`builtInTemplates.${num}.content`),
      tags: t(`builtInTemplates.${num}.tags`, { returnObjects: true }) || [],
      popularity: template.popularity,
      isBuiltIn: true
    };
  });
};

export default function Templates() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("builtin");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  
  // Custom templates state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // New template form
  const [newTemplate, setNewTemplate] = useState({
    title: "",
    description: "",
    content: "",
    category: "custom",
    tags: ""
  });

  // Template categories with icons (translated)
  const getCategories = () => [
    { id: "all", label: t('templatesPage.categories.all'), icon: FileText },
    { id: "ecommerce", label: t('templatesPage.categories.ecommerce'), icon: ShoppingBag },
    { id: "restaurants", label: t('templatesPage.categories.restaurants'), icon: Utensils },
    { id: "services", label: t('templatesPage.categories.services'), icon: Briefcase },
    { id: "education", label: t('templatesPage.categories.education'), icon: GraduationCap },
    { id: "health", label: t('templatesPage.categories.health'), icon: Heart },
    { id: "realestate", label: t('templatesPage.categories.realestate'), icon: Home },
    { id: "automotive", label: t('templatesPage.categories.automotive'), icon: Car },
    { id: "travel", label: t('templatesPage.categories.travel'), icon: Plane },
    { id: "tech", label: t('templatesPage.categories.tech'), icon: Smartphone },
    { id: "events", label: t('templatesPage.categories.events'), icon: Calendar },
    { id: "custom", label: t('templatesPage.categories.custom'), icon: Sparkles },
  ];

  const translatedCategories = getCategories();
  
  // Get bilingual built-in templates
  const builtInTemplates = getBuiltInTemplates(t);

  // Load custom templates
  useEffect(() => {
    loadCustomTemplates();
  }, []);

  const loadCustomTemplates = async () => {
    setLoading(true);
    try {
      const response = await getCustomTemplates();
      setCustomTemplates(response.data.data || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.title.trim() || !newTemplate.content.trim()) {
      toast.error(t('templatesPage.enterTitleAndContent'));
      return;
    }

    try {
      const templateData = {
        ...newTemplate,
        tags: newTemplate.tags.split(",").map(tag => tag.trim()).filter(Boolean)
      };
      
      if (editingTemplate) {
        await updateCustomTemplate(editingTemplate.id, templateData);
        toast.success(t('templatesPage.templateUpdated'));
      } else {
        await createCustomTemplate(templateData);
        toast.success(t('templatesPage.templateCreated'));
      }
      
      setShowCreateDialog(false);
      setEditingTemplate(null);
      setNewTemplate({ title: "", description: "", content: "", category: "custom", tags: "" });
      loadCustomTemplates();
    } catch (error) {
      toast.error(t('templatesPage.errorSaving'));
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    try {
      await deleteCustomTemplate(templateToDelete.id);
      toast.success(t('templatesPage.templateDeleted'));
      setShowDeleteDialog(false);
      setTemplateToDelete(null);
      loadCustomTemplates();
    } catch (error) {
      toast.error(t('templatesPage.errorDeleting'));
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setNewTemplate({
      title: template.title,
      description: template.description || "",
      content: template.content,
      category: template.category || "custom",
      tags: (template.tags || []).join(", ")
    });
    setShowCreateDialog(true);
  };

  const handleToggleFavorite = async (template) => {
    try {
      await toggleTemplateFavorite(template.id);
      loadCustomTemplates();
      toast.success(template.is_favorite ? t('templatesPage.removedFromFavorites') : t('templatesPage.addedToFavorites'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const currentTemplates = activeTab === "builtin" ? builtInTemplates : customTemplates;
  
  const filteredTemplates = currentTemplates.filter(template => {
    const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
    const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.tags || []).some(tag => tag.includes(searchTerm));
    return matchesCategory && matchesSearch;
  });

  const handleCopy = (template) => {
    navigator.clipboard.writeText(template.content);
    setCopiedId(template.id);
    toast.success(t('templatesPage.templateCopied'));
    setTimeout(() => setCopiedId(null), 2000);
    
    // Increment use count for custom templates
    if (!template.isBuiltIn && template.id) {
      incrementTemplateUse(template.id).catch(() => {});
    }
  };

  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    setEditedContent(template.content);
    
    // Increment use count for custom templates
    if (!template.isBuiltIn && template.id) {
      incrementTemplateUse(template.id).catch(() => {});
    }
  };

  const handleCopyEdited = () => {
    navigator.clipboard.writeText(editedContent);
    toast.success(t('templatesPage.editedTextCopied'));
  };

  const handleSaveAsCustom = async () => {
    if (!selectedTemplate) return;
    
    try {
      await createCustomTemplate({
        title: selectedTemplate.title + " (modified)",
        description: selectedTemplate.description,
        content: editedContent,
        category: selectedTemplate.category,
        tags: selectedTemplate.tags || []
      });
      toast.success(t('templatesPage.savedToMyTemplates'));
      loadCustomTemplates();
    } catch (error) {
      toast.error(t('templatesPage.errorSaving'));
    }
  };

  const getCategoryIcon = (categoryId) => {
    const category = translatedCategories.find(c => c.id === categoryId);
    return category ? category.icon : FileText;
  };

  const getCategoryLabel = (categoryId) => {
    const category = translatedCategories.find(c => c.id === categoryId);
    return category ? category.label : categoryId;
  };

  const favoriteTemplates = customTemplates.filter(t => t.is_favorite);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('templatesPage.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('templatesPage.subtitle')}</p>
        </div>
        <Button onClick={() => {
          setEditingTemplate(null);
          setNewTemplate({ title: "", description: "", content: "", category: "custom", tags: "" });
          setShowCreateDialog(true);
        }} data-testid="create-template-btn">
          <Plus className="w-4 h-4 me-2" />
          {t('templatesPage.createNewTemplate')}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="builtin" data-testid="tab-builtin">
            <FileText className="w-4 h-4 me-2" />
            {t('templatesPage.builtInTemplates')} ({builtInTemplates.length})
          </TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom">
            <FolderHeart className="w-4 h-4 me-2" />
            {t('templatesPage.myTemplates')} ({customTemplates.length})
          </TabsTrigger>
        </TabsList>

        {/* Favorites Section (only in custom tab) */}
        {activeTab === "custom" && favoriteTemplates.length > 0 && (
          <Card className="mt-4 border-yellow-500/30 bg-yellow-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                {t('templatesPage.favorites')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {favoriteTemplates.map(template => (
                  <Badge 
                    key={template.id} 
                    variant="secondary" 
                    className="cursor-pointer hover:bg-primary/20"
                    onClick={() => handleUseTemplate(template)}
                  >
                    {template.title}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mt-4">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('templatesPage.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pe-10"
              data-testid="search-templates-input"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="overflow-x-auto pb-2 mt-4">
          <div className="flex gap-2 min-w-max">
            {translatedCategories.map((category) => {
              const Icon = category.icon;
              const isActive = selectedCategory === category.id;
              return (
                <Button
                  key={category.id}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                  className={isActive ? "bg-primary" : ""}
                  data-testid={`category-${category.id}`}
                >
                  <Icon className="w-4 h-4 me-1" />
                  {category.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Built-in Templates Content */}
        <TabsContent value="builtin" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => {
              const CategoryIcon = getCategoryIcon(template.category);
              return (
                <Card 
                  key={template.id} 
                  className="card-hover group"
                  data-testid={`template-card-${template.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <CategoryIcon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{template.title}</CardTitle>
                          <CardDescription className="text-xs">
                            {getCategoryLabel(template.category)}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        {template.popularity}%
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                      {template.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(template.tags || []).map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="bg-muted/50 p-3 rounded-lg mb-4 max-h-32 overflow-hidden relative">
                      <pre className="text-xs whitespace-pre-wrap font-sans text-muted-foreground">
                        {template.content.slice(0, 150)}...
                      </pre>
                      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/50 to-transparent" />
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleUseTemplate(template)}
                        data-testid={`use-template-${template.id}`}
                      >
                        <Sparkles className="w-4 h-4 me-1" />
                        {t('templatesPage.use')}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleCopy(template)}
                        data-testid={`copy-template-${template.id}`}
                      >
                        {copiedId === template.id ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Custom Templates Content */}
        <TabsContent value="custom" className="mt-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">{t('templatesPage.loading')}</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <FolderHeart className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-bold mb-2">{t('templatesPage.noCustomTemplates')}</h3>
                  <p className="text-muted-foreground mb-4">{t('templatesPage.createFirstTemplate')}</p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 me-2" />
                    {t('templatesPage.createNewTemplate')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => {
                const CategoryIcon = getCategoryIcon(template.category);
                return (
                  <Card 
                    key={template.id} 
                    className="card-hover group"
                    data-testid={`custom-template-${template.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <CategoryIcon className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{template.title}</CardTitle>
                            <CardDescription className="text-xs">
                              {getCategoryLabel(template.category)}
                            </CardDescription>
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => handleToggleFavorite(template)}
                        >
                          <Star className={`w-4 h-4 ${template.is_favorite ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'}`} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {template.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-1 mb-4">
                        {(template.tags || []).map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <div className="bg-muted/50 p-3 rounded-lg mb-4 max-h-32 overflow-hidden relative">
                        <pre 
                          className="text-xs whitespace-pre-wrap font-sans text-muted-foreground"
                          dir={detectTextDirection(template.content)}
                          style={{ textAlign: detectTextDirection(template.content) === 'rtl' ? 'right' : 'left' }}
                        >
                          {template.content.slice(0, 150)}...
                        </pre>
                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/50 to-transparent" />
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <BarChart2 className="w-3 h-3" />
                          {template.use_count || 0} {t('templatesPage.usage')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(template.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleUseTemplate(template)}
                        >
                          <Sparkles className="w-4 h-4 me-1" />
                          {t('templatesPage.use')}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditTemplate(template)}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleCopy(template)}
                        >
                          {copiedId === template.id ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            setTemplateToDelete(template);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* No Results */}
      {filteredTemplates.length === 0 && activeTab === "builtin" && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-bold mb-2">{t('templatesPage.noResults')}</h3>
              <p className="text-muted-foreground">{t('templatesPage.tryDifferentSearch')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Editor Dialog */}
      <Dialog open={!!selectedTemplate} onOpenChange={() => setSelectedTemplate(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              {selectedTemplate?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4 mt-4">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  {t('templatesPage.tip')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('templatesPage.tipContent')}
                </p>
              </div>

              <div className="form-group">
                <label className="text-sm font-medium mb-2 block">{t('templatesPage.editTemplate')}:</label>
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  rows={15}
                  className="font-mono text-sm"
                  data-testid="template-editor"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleCopyEdited} className="flex-1" data-testid="copy-edited-btn">
                  <Copy className="w-4 h-4 me-2" />
                  {t('templatesPage.copyEditedText')}
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleSaveAsCustom}
                  data-testid="save-as-custom-btn"
                >
                  <Save className="w-4 h-4 me-2" />
                  {t('templatesPage.saveToMyTemplates')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setEditedContent(selectedTemplate.content)}
                >
                  {t('templatesPage.reset')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[600px]" dir="rtl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? t('templatesPage.editTemplate') : t('templatesPage.createNewTemplate')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="form-group">
              <Label>{t('templatesPage.templateTitle')} *</Label>
              <Input
                value={newTemplate.title}
                onChange={(e) => setNewTemplate({...newTemplate, title: e.target.value})}
                placeholder={t('templatesPage.templateTitlePlaceholder')}
                data-testid="new-template-title"
              />
            </div>

            <div className="form-group">
              <Label>{t('templatesPage.description')}</Label>
              <Input
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                placeholder={t('templatesPage.descriptionPlaceholder')}
              />
            </div>

            <div className="form-group">
              <Label>{t('templatesPage.category')}</Label>
              <Select 
                value={newTemplate.category} 
                onValueChange={(value) => setNewTemplate({...newTemplate, category: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {translatedCategories.filter(c => c.id !== "all").map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label>{t('templatesPage.content')} *</Label>
              <Textarea
                value={newTemplate.content}
                onChange={(e) => setNewTemplate({...newTemplate, content: e.target.value})}
                placeholder={t('templatesPage.contentPlaceholder')}
                rows={10}
                className="font-mono text-sm"
                data-testid="new-template-content"
              />
            </div>

            <div className="form-group">
              <Label>{t('templatesPage.tags')}</Label>
              <Input
                value={newTemplate.tags}
                onChange={(e) => setNewTemplate({...newTemplate, tags: e.target.value})}
                placeholder={t('templatesPage.tagsPlaceholder')}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t('templatesPage.cancel')}
            </Button>
            <Button onClick={handleCreateTemplate} data-testid="save-template-btn">
              <Save className="w-4 h-4 me-2" />
              {editingTemplate ? t('templatesPage.saveChanges') : t('templatesPage.saveTemplate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('templatesPage.deleteTemplate')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('templatesPage.deleteConfirm')} "{templateToDelete?.title}"? {t('templatesPage.cannotUndo')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('templatesPage.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTemplate}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stats */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-6 justify-center">
            <div className="text-center">
              <p className="text-3xl font-black text-primary">{builtInTemplates.length}</p>
              <p className="text-sm text-muted-foreground">{t('templatesPage.readyTemplate')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-black text-primary">{customTemplates.length}</p>
              <p className="text-sm text-muted-foreground">{t('templatesPage.customTemplate')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-black text-primary">{translatedCategories.length - 1}</p>
              <p className="text-sm text-muted-foreground">{t('templatesPage.differentCategories')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
